/**
 * One source of truth for Office.js host capabilities (brief §21, P6). The add-in
 * targets Mac + Windows DESKTOP; web/older hosts are best-effort. Rather than
 * scattering `isSetSupported(...)` / `typeof fn === 'function'` probes across the
 * engine, typography and navigation, they consult this matrix so degradation is
 * consistent and observable.
 *
 * `computeCapabilities` is PURE (takes a probe) → unit-tested; `detectCapabilities`
 * wires it to the live Office runtime (guarded, never throws).
 */

export type OfficePlatform = 'desktop' | 'web' | 'mobile' | 'unknown';

export interface OfficeCapabilities {
  platform: OfficePlatform;
  /** Toggle tracked changes (changeTrackingMode) — WordApi 1.1. Core to redlining. */
  trackedChanges: boolean;
  /** Range/Paragraph.getOoxml — WordApi 1.1. Structural field gate + §19 verification. */
  getOoxml: boolean;
  /** Range.getReviewedText — WordApi 1.4. Text round-trip verification. */
  getReviewedText: boolean;
  /** Per-run proofing language (Range.languageId) — WordApiDesktop 1.3. Quote convention. */
  proofingLanguage: boolean;
  /** Range.search — WordApi 1.1. Locating edits. Core to redlining. */
  search: boolean;
  /** Window.setFocus — WordApiDesktop 1.4. Hand keyboard focus back to the
   *  document after a pane action (see utils/office/focus.ts). */
  documentFocus: boolean;
  /** One JS context for pane + ribbon + shortcuts — SharedRuntime 1.1. Absent on
   *  perpetual/LTSC Office, frozen channels and Word on the web: the manifest no
   *  longer GATES on it (see public/manifest.xml), so the add-in loads there and
   *  ribbon commands run in the classic function file instead. */
  sharedRuntime: boolean;
  /** Document-wide add-in shortcuts (ExtendedOverrides + Office.actions) —
   *  KeyboardShortcuts 1.1. Needs the shared runtime in practice. */
  keyboardShortcuts: boolean;
}

export interface CapabilityProbe {
  /** Raw Office platform string (e.g. 'PC','Mac','OfficeOnline','iOS'), if known. */
  platform?: string;
  /** Requirement-set support check (Office.context.requirements.isSetSupported). */
  supports: (set: string, version: string) => boolean;
}

function mapPlatform(raw?: string): OfficePlatform {
  if (!raw) return 'unknown';
  if (raw === 'PC' || raw === 'Mac') return 'desktop';
  if (raw === 'OfficeOnline') return 'web';
  if (raw === 'iOS' || raw === 'Android') return 'mobile';
  return 'unknown';
}

/** Pure: derive the capability matrix from a probe. */
export function computeCapabilities(probe: CapabilityProbe): OfficeCapabilities {
  const has = (set: string, version: string): boolean => {
    try {
      return !!probe.supports(set, version);
    } catch {
      return false;
    }
  };
  return {
    platform: mapPlatform(probe.platform),
    trackedChanges: has('WordApi', '1.1'),
    getOoxml: has('WordApi', '1.1'),
    getReviewedText: has('WordApi', '1.4'),
    proofingLanguage: has('WordApiDesktop', '1.3'),
    search: has('WordApi', '1.1'),
    documentFocus: has('WordApiDesktop', '1.4'),
    sharedRuntime: has('SharedRuntime', '1.1'),
    keyboardShortcuts: has('KeyboardShortcuts', '1.1'),
  };
}

/** True when the host supports the FULL safe-redline path (gate + both verifications). */
export function supportsFullRedline(c: OfficeCapabilities): boolean {
  return c.trackedChanges && c.search && c.getOoxml && c.getReviewedText;
}

/**
 * Why add-in keyboard shortcuts cannot work on this host, or null when they can.
 * Distinct from `degradationNotice` (which is about redline SAFETY): shortcuts
 * are a convenience, and losing them is the accepted price of letting older
 * Word builds load the add-in at all.
 */
export function shortcutUnavailabilityReason(c: OfficeCapabilities): string | null {
  if (c.sharedRuntime && c.keyboardShortcuts) return null;
  if (c.platform === 'web') {
    return 'Word on the web does not support add-in keyboard shortcuts. The ribbon buttons in the V14 group do the same things.';
  }
  return 'This Word build does not support add-in keyboard shortcuts — they need Microsoft 365 Apps (subscription) Word, from build 16.0.13215 on Windows or 16.45 on Mac. Perpetual Office (2019/2021/2024) never gets them. The ribbon buttons in the V14 group do the same things.';
}

/**
 * Human-readable degradation notice for the current host, or null when the full
 * safe-redline path is available. Surfaced so the user knows when a check is
 * weaker on this host (e.g. Word on the web).
 */
export function degradationNotice(c: OfficeCapabilities): string | null {
  if (supportsFullRedline(c)) return null;
  const missing: string[] = [];
  if (!c.trackedChanges || !c.search) return 'This host does not support tracked-change editing; AI edits cannot be applied here. Use Word on Windows or Mac.';
  if (!c.getOoxml) missing.push('embedded-field protection');
  if (!c.getReviewedText) missing.push('post-apply verification');
  if (missing.length === 0) return null;
  return `On this host, ${missing.join(' and ')} ${missing.length > 1 ? 'are' : 'is'} unavailable — edits still apply as tracked changes, but with reduced safety checks. For full protection, use Word on Windows or Mac.`;
}

let cached: OfficeCapabilities | null = null;

/** Detect capabilities from the live Office runtime (guarded, memoised). */
export function detectCapabilities(): OfficeCapabilities {
  if (cached) return cached;
  let platform: string | undefined;
  let supports: (set: string, version: string) => boolean = () => false;
  try {
    platform = (Office?.context?.platform as unknown as string) ?? undefined;
    const req = Office?.context?.requirements;
    if (req?.isSetSupported) supports = (s, v) => req.isSetSupported(s, v);
  } catch {
    /* Office unavailable (e.g. unit test / non-Office host) → all false */
  }
  cached = computeCapabilities({ platform, supports });
  const notice = degradationNotice(cached);
  console.info('[Capabilities]', cached, notice ? `— ${notice}` : '— full redline support');
  return cached;
}

/** Reset the memoised value — tests only. */
export function resetCapabilitiesCache(): void {
  cached = null;
}
